import { ReactNode, useCallback, useMemo, useRef, useState } from "react";

import { ELEMENT_IDS } from "features/bookmarks/utils";

import { DOCK_EDGE_MARGIN, DOCK_LEFT, DOCK_MAX_WIDTH, DOCK_MIN_WIDTH, DOCK_PANEL_WIDTH } from "./constants";
import {
    computeInsertionIndex,
    DockDragContext,
    type DockDragController,
    type DockDragSource,
    type DockDragState,
    type DockLivePosition,
    type DockLiveResize,
    type DockLiveWidth,
    type DockSlotMeasurement,
    IDLE_DOCK_DRAG_STATE,
} from "./dockDragContext";
import { useLiveStore } from "./useLiveStore";

function statesEqual(a: DockDragState, b: DockDragState): boolean {
    return (
        a.isDragging === b.isDragging &&
        a.source === b.source &&
        a.draggingId === b.draggingId &&
        a.isInZone === b.isInZone &&
        a.insertionIndex === b.insertionIndex
    );
}

/**
 * Holds transient dock drag state. Mirrors to React state (for re-renders) and a
 * ref (for synchronous reads in event handlers — use `controller.getState()`).
 */
export function DockDragProvider({ children }: { children: ReactNode }) {
    const zoneRectRef = useRef<DOMRect | null>(null);
    const slotsRef = useRef<DockSlotMeasurement[]>([]);
    const stateRef = useRef<DockDragState>(IDLE_DOCK_DRAG_STATE);
    const [state, setStateInternal] = useState<DockDragState>(IDLE_DOCK_DRAG_STATE);

    const {
        set: setLivePosition,
        get: getLivePosition,
        subscribe: subscribeLivePosition,
    } = useLiveStore<DockLivePosition>();
    const {
        set: setLiveResize,
        get: getLiveResize,
        subscribe: subscribeLiveResize,
    } = useLiveStore<DockLiveResize>((a, b) => a.id === b.id && a.height === b.height);
    const {
        set: setLiveWidth,
        get: getLiveWidth,
        subscribe: subscribeLiveWidth,
    } = useLiveStore<DockLiveWidth>((a, b) => a.width === b.width);

    // Committed dock column width — React state so consumers re-render on commit.
    const [dockWidth, setDockWidthState] = useState(DOCK_PANEL_WIDTH);
    const dockWidthRef = useRef(DOCK_PANEL_WIDTH);

    const setDockWidth = useCallback((width: number) => {
        const clamped = Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, width));
        dockWidthRef.current = clamped;
        setDockWidthState(clamped);
    }, []);

    const commit = useCallback((next: DockDragState) => {
        if (statesEqual(stateRef.current, next)) {
            return;
        }
        stateRef.current = next;
        setStateInternal(next);
    }, []);

    const registerZone = useCallback((rect: DOMRect | null) => {
        zoneRectRef.current = rect;
    }, []);

    const registerSlots = useCallback((slots: DockSlotMeasurement[]) => {
        slotsRef.current = slots;
    }, []);

    const beginDrag = useCallback(
        (source: DockDragSource, id: string) => {
            commit({
                isDragging: true,
                source,
                draggingId: id,
                isInZone: false,
                insertionIndex: null,
            });
        },
        [commit],
    );

    const updatePointer = useCallback(
        (clientX: number, clientY: number) => {
            const prev = stateRef.current;
            if (!prev.isDragging) {
                return;
            }
            const renderContainer = document.getElementById(ELEMENT_IDS.renderContainer);
            const zoneRect = renderContainer
                ? (() => {
                      const renderRect = renderContainer.getBoundingClientRect();
                      const left = renderRect.left + DOCK_LEFT;
                      const right = left + dockWidthRef.current;
                      const top = renderRect.top + DOCK_EDGE_MARGIN;
                      const bottom = renderRect.bottom - DOCK_EDGE_MARGIN;
                      return { left, right, top, bottom };
                  })()
                : zoneRectRef.current;
            if (!zoneRect) {
                commit({ ...prev, isInZone: false, insertionIndex: null });
                return;
            }
            const inZone =
                clientX >= zoneRect.left &&
                clientX <= zoneRect.right &&
                clientY >= zoneRect.top &&
                clientY <= zoneRect.bottom;
            if (!inZone) {
                commit({ ...prev, isInZone: false, insertionIndex: null });
                return;
            }
            const yInZone = clientY - zoneRect.top;
            const insertionIndex = computeInsertionIndex(yInZone, slotsRef.current);
            commit({ ...prev, isInZone: true, insertionIndex });
        },
        [commit],
    );

    const endDrag = useCallback(() => {
        // Clear live position so a stale value can't bleed into the next gesture.
        setLivePosition(null);
        commit(IDLE_DOCK_DRAG_STATE);
    }, [commit, setLivePosition]);

    const getState = useCallback(() => stateRef.current, []);

    const controller = useMemo<DockDragController>(
        () => ({
            state,
            getState,
            registerZone,
            registerSlots,
            beginDrag,
            updatePointer,
            endDrag,
            setLivePosition,
            getLivePosition,
            subscribeLivePosition,
            setLiveResize,
            getLiveResize,
            subscribeLiveResize,
            dockWidth,
            setDockWidth,
            setLiveWidth,
            getLiveWidth,
            subscribeLiveWidth,
        }),
        [
            state,
            getState,
            registerZone,
            registerSlots,
            beginDrag,
            updatePointer,
            endDrag,
            setLivePosition,
            getLivePosition,
            subscribeLivePosition,
            setLiveResize,
            getLiveResize,
            subscribeLiveResize,
            dockWidth,
            setDockWidth,
            setLiveWidth,
            getLiveWidth,
            subscribeLiveWidth,
        ],
    );

    return <DockDragContext.Provider value={controller}>{children}</DockDragContext.Provider>;
}
